import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useIsMobileViewport } from '../../hooks/useIsMobile';
import BrandLogo from '../BrandLogo';
import ProfileHeaderActions from '../ProfileHeaderActions';

export default function AppShell({
  children,
  navItems,
  showBack,
  backLabel = 'Dashboard',
  backPath = '/dashboard',
  collapsible = false,
  defaultCollapsed = false,
}) {
  const { logOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobileViewport();
  const [menuOpen, setMenuOpen] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(Boolean(defaultCollapsed));

  async function handleLogOut() {
    await logOut();
    navigate('/');
  }

  function handleNav(path) {
    if (path) navigate(path);
    setMenuOpen(false);
  }

  const collapsed = collapsible && navCollapsed && !isMobile;

  const sidebar = (
    <aside
      className={`sba-sidebar${menuOpen ? ' is-open' : ''}${collapsed ? ' is-collapsed' : ''}`}
    >
      {collapsible && !isMobile && (
        <button
          type="button"
          className="sba-sidebar-collapse-btn"
          aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          title={collapsed ? 'Expand menu' : 'Collapse menu'}
          onClick={() => setNavCollapsed((v) => !v)}
        >
          <span className="material-symbols-outlined">
            {collapsed ? 'keyboard_double_arrow_right' : 'keyboard_double_arrow_left'}
          </span>
        </button>
      )}

      {showBack && (
        <button
          type="button"
          className="sba-sidebar-back"
          onClick={() => handleNav(backPath)}
          title={backLabel}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>
            arrow_back
          </span>
          <span className="sba-sidebar-label">{backLabel}</span>
        </button>
      )}

      <div className="sba-sidebar-logo">
        <span className="sba-sidebar-logo-icon">
          <BrandLogo size={collapsed ? 36 : 44} />
        </span>
        <span className="sba-sidebar-label">Success Bridge Academy</span>
      </div>
      <span className="sba-sidebar-portal-tag sba-sidebar-label">Student portal</span>
      <nav className="sba-sidebar-nav">
        {navItems.map(({ icon, label, path, active, accent, locked }) => {
          const isActive = active ?? (path && location.pathname === path);
          const isLocked = Boolean(locked) && !path;
          return (
            <button
              key={label}
              type="button"
              className={`sba-nav-item${isActive ? (accent ? ' active-accent' : ' active') : ''}${
                isLocked || locked ? ' is-locked' : ''
              }`}
              onClick={() => {
                if (locked && !path) return;
                handleNav(path);
              }}
              disabled={!path}
              title={locked && !path ? 'Unlock with an active plan' : label}
            >
              <span className="material-symbols-outlined" style={{ fontSize: 22 }}>
                {icon}
              </span>
              <span className="sba-sidebar-label" style={{ flex: 1, textAlign: 'left' }}>
                {label}
              </span>
              {locked && !path ? (
                <span className="material-symbols-outlined sba-sidebar-label" style={{ fontSize: 16, opacity: 0.7 }}>
                  lock
                </span>
              ) : null}
            </button>
          );
        })}
      </nav>
      <button type="button" className="sba-logout-btn" onClick={handleLogOut} title="Sign Out">
        <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
          logout
        </span>
        <span className="sba-sidebar-label">Sign Out</span>
      </button>
    </aside>
  );

  return (
    <div className={`sba-app${collapsed ? ' sba-app--nav-collapsed' : ''}`}>
      {isMobile && (
        <header className="sba-mobile-bar">
          <button
            type="button"
            className="sba-mobile-menu-btn"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMenuOpen((o) => !o)}
          >
            <span className="material-symbols-outlined">
              {menuOpen ? 'close' : 'menu'}
            </span>
          </button>
          <div className="sba-mobile-bar-brand">
            <BrandLogo size={32} />
            <span>Success Bridge</span>
          </div>
          <ProfileHeaderActions compact variant="dark" />
        </header>
      )}

      {isMobile && menuOpen && (
        <button
          type="button"
          className="sba-sidebar-backdrop"
          aria-label="Close menu"
          onClick={() => setMenuOpen(false)}
        />
      )}

      {sidebar}

      <main className="sba-main">
        {!isMobile && (
          <div className="sba-main-header">
            <div className="sba-main-header-spacer" />
            <ProfileHeaderActions />
          </div>
        )}
        {children}
      </main>
    </div>
  );
}
